import React from 'react';
import styled, { css, FlattenSimpleInterpolation, CSSProp } from 'styled-components';
import { Colors, Fonts } from '@prezi/dragon';
import { $switchCaseStrict } from 'dolla-switch';

type ThemeName = 'normal' | 'link' | 'mono' | 'label' | 'caption';
type ElementType = 'span' | 'label' | 'p' | 'a' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type Props = React.HTMLAttributes<HTMLElement> & {
    type: ElementType;
    theme?: ThemeName;
    disabled?: boolean;
    href?: string;
    target?: string;
    children: any;
};

const baseCss = css`
    font-size: 1em;
    line-height: 1.2;
    letter-spacing: calc(1 / 56em);
    margin-bottom: 0.6em;
    && {
        text-decoration: none;
    }
`;

const normalThemeCss = css`
    ${baseCss}
    color: ${Colors.neutral600};
    font-family: ${Fonts.ralewaySemiBold};
`;

const linkThemeCss = css`
    ${baseCss}
    color: ${Colors.blue500};
    font-family: ${Fonts.ralewayBold};
    transition: color 0.2s;
    &[aria-disabled='true'] {
        color: ${Colors.blue200};
        cursor: not-allowed;
        pointer-events: none;
    }
    &:hover {
        cursor: pointer;
        color: ${Colors.blue400};
    }
`;

const monoThemeCss = css`
    ${baseCss}
    color: ${Colors.neutral600};
    font-family: Courier, monospace;
`;

const labelThemeCss = css`
    font-family: ${Fonts.ralewayBold};
    font-size: 0.8em;
    line-height: 1.5;
    margin-bottom: 0.6em;
    letter-spacing: calc(1 / 56em);
    text-transform: uppercase;
`;

const captionThemeCss = css`
    font-size: 0.8em;
    line-height: 1.5;
    letter-spacing: calc(1 / 56em);
`;

const getDefaultTheme = (type: ElementType) =>
    $switchCaseStrict<ElementType, ThemeName>(type, {
        span: () => 'normal',
        label: () => 'label',
        p: () => 'normal',
        a: () => 'link',
        h1: () => 'normal',
        h2: () => 'normal',
        h3: () => 'normal',
        h4: () => 'normal',
        h5: () => 'normal',
        h6: () => 'normal',
    });

const getThemeCss = (theme: ThemeName) =>
    $switchCaseStrict<ThemeName, FlattenSimpleInterpolation>(theme, {
        normal: () => normalThemeCss,
        link: () => linkThemeCss,
        label: () => labelThemeCss,
        mono: () => monoThemeCss,
        caption: () => captionThemeCss,
    });

const Span = styled.span<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
`;

const Label = styled.div<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
`;

const Paragraph = styled.p<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
`;

const Anchor = styled.a<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
`;

const H1 = styled.h1<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
    font-size: 2.4em;
`;

const H2 = styled.h2<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
    font-size: 2em;
`;

const H3 = styled.h3<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
    font-size: 1.8em;
`;

const H4 = styled.h4<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
    font-size: 1.4em;
`;

const H5 = styled.h5<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
    font-size: 1.2em;
`;

const H6 = styled.h6<{ themeCss: CSSProp }>`
    ${p => p.themeCss}
`;

export default ({ type = 'span', theme, disabled, ...rest }: Props) => {
    const selectedTheme = theme || getDefaultTheme(type);
    const themeCss = getThemeCss(selectedTheme);

    return $switchCaseStrict<ElementType, React.ReactElement>(type, {
        span: () => <Span themeCss={themeCss} {...rest} />,
        label: () => <Label themeCss={themeCss} {...rest} />,
        p: () => <Paragraph themeCss={themeCss} {...rest} />,
        a: () => <Anchor aria-disabled={disabled} themeCss={themeCss} {...rest} />,
        h1: () => <H1 themeCss={themeCss} {...rest} />,
        h2: () => <H2 themeCss={themeCss} {...rest} />,
        h3: () => <H3 themeCss={themeCss} {...rest} />,
        h4: () => <H4 themeCss={themeCss} {...rest} />,
        h5: () => <H5 themeCss={themeCss} {...rest} />,
        h6: () => <H6 themeCss={themeCss} {...rest} />,
    });
};
